/**
 * "Before you resume, prove the credential is still good."
 *
 * Issue #3072. A long-running Deep Explorer turn parks the server in a
 * human-in-the-loop wait (plan decision / ask_user) with a 300 s budget. When
 * the backend worker recycles mid-run, the client resumes by opening a new
 * socket — and every resume path was handing that socket the token it captured
 * when the run *started*. On a long run that token has often expired, so the
 * reconnect failed auth, the decision never reached the waiting run, and the
 * user watched a "Planning…" spinner until the wait timed out and cancelled.
 *
 * The fix is to re-acquire before resuming, through the existing silent path:
 * `registerAuthSessionForCurrentMode` (auth-api.ts) registers a driver whose
 * `refresh` is MSAL `acquireTokenSilent({ forceRefresh: true })` under EasyAuth's
 * `/.auth/refresh` equivalent, and the coordinator gives us its single-flight,
 * identity-guarded, epoch-bounded wrapper for free. Nothing new is invented
 * here; this module is only the resume-shaped entry point into it.
 *
 * When the silent path cannot recover, the coordinator has already fired the
 * one deduplicated re-login navigation for that credential. The caller's job is
 * then to stop — surfacing the prompt and *not* dispatching the resume — rather
 * than to retry with a token the server has already rejected.
 */
import { logger } from "@/lib/logger"

import {
  captureAuthSnapshot,
  getManagedCredential,
  hasRegisteredAuthSession,
  recoverFromUnauthorized,
} from "./auth-refresh-coordinator"

export type ResumeCredential =
  /** Safe to resume. `token` is the credential to send (undefined in cookie mode). */
  | { status: "ok"; token: string | undefined }
  /**
   * Silent recovery is impossible. The coordinator has already started the
   * app's re-login navigation; the caller must not dispatch with the stale
   * credential, and should tell the user why the resume stopped.
   */
  | { status: "reauth-required"; reason: string }
  /** A different session was established meanwhile — this resume belongs to nobody. */
  | { status: "session-changed" }

/**
 * Re-acquire the credential before resuming a dropped stream.
 *
 * `fallbackToken` is what the caller would have sent (typically the token it
 * captured when the run started). It is returned unchanged only when there is
 * nothing to refresh — no registered session (boot ordering, login disabled) —
 * never as a consolation prize after a failed refresh.
 */
export async function reacquireCredentialForResume(
  fallbackToken?: string,
  options: { signal?: AbortSignal | null } = {}
): Promise<ResumeCredential> {
  if (!hasRegisteredAuthSession()) {
    // Nothing is managing a credential for this page yet; the caller's own
    // token is all there is, and a 401 on it still routes through
    // `fetchWithAuthRetry` / the WS auth-error path as before.
    return { status: "ok", token: fallbackToken }
  }

  const outcome = await recoverFromUnauthorized(captureAuthSnapshot(), { signal: options.signal })

  switch (outcome.status) {
    case "refreshed":
    case "already-refreshed":
      // Prefer the credential the coordinator now manages: another request may
      // have refreshed it while we waited, in which case ours is already dead.
      return { status: "ok", token: getManagedCredential() ?? fallbackToken }
    case "unavailable":
      return { status: "ok", token: fallbackToken }
    case "session-changed":
      return { status: "session-changed" }
    case "cancelled":
      // The caller aborted (navigated away, newer resume took over). Not an
      // auth problem — say so, so nobody prompts for a re-login over it.
      return { status: "session-changed" }
    case "failed":
      logger.warn("Resume blocked: could not silently re-acquire credentials", {
        reason: outcome.reason,
      })
      return { status: "reauth-required", reason: outcome.reason }
  }
}
